import type { ScheduleEntryInput, StaffScheduleEntry } from "@bednbody/types";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { RadioGroup, RadioGroupItem } from "@bednbody/ui/components/radio-group";
import { Textarea } from "@bednbody/ui/components/textarea";
import { useEffect, useState } from "react";
import { dutyInput, timeInManila } from "../schedule-display";
import type { ScheduleStaff } from "../schedules.hooks";

interface ScheduleCellDialogProps {
  open: boolean;
  staff: ScheduleStaff | null;
  scheduleDate: string | null;
  entry?: StaffScheduleEntry;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (entry: ScheduleEntryInput) => void;
  onClear: () => void;
}

export function ScheduleCellDialog({
  open,
  staff,
  scheduleDate,
  entry,
  pending,
  onOpenChange,
  onSave,
  onClear,
}: ScheduleCellDialogProps) {
  const [kind, setKind] = useState<"duty" | "rest_day">("duty");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setKind(entry?.kind ?? "duty");
    setStartTime(timeInManila(entry?.dutyStart ?? null));
    setEndTime(timeInManila(entry?.dutyEnd ?? null));
    setNote(entry?.availabilityNote ?? "");
  }, [entry, open]);

  if (!staff || !scheduleDate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{staff.name}</DialogTitle>
          <DialogDescription>
            {scheduleDate} · {staff.hubName}
          </DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-3">
          <legend className="mb-2 text-sm font-medium">Day status</legend>
          <RadioGroup
            value={kind}
            onValueChange={(value) => {
              if (value === "duty" || value === "rest_day") setKind(value);
            }}
            className="flex gap-5"
          >
            <label htmlFor="schedule-kind-duty" className="inline-flex items-center gap-2">
              <RadioGroupItem id="schedule-kind-duty" value="duty" />
              Duty
            </label>
            <label htmlFor="schedule-kind-rest-day" className="inline-flex items-center gap-2">
              <RadioGroupItem id="schedule-kind-rest-day" value="rest_day" />
              Rest Day
            </label>
          </RadioGroup>
        </fieldset>

        {kind === "duty" ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="schedule-duty-start">Duty start</Label>
              <Input
                id="schedule-duty-start"
                type="time"
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="schedule-duty-end">Duty end</Label>
              <Input
                id="schedule-duty-end"
                type="time"
                value={endTime}
                onChange={(event) => setEndTime(event.target.value)}
                required
              />
            </div>
            <p className="col-span-2 text-xs text-muted-foreground">
              An end time earlier than the start is treated as ending the next day.
            </p>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="schedule-note">Availability details (optional)</Label>
          <Textarea
            id="schedule-note"
            maxLength={500}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <DialogFooter>
          {entry ? (
            <Button type="button" variant="destructive" disabled={pending} onClick={onClear}>
              Clear cell
            </Button>
          ) : null}
          <Button
            type="button"
            disabled={pending || (kind === "duty" && (!startTime || !endTime))}
            onClick={() =>
              onSave(
                kind === "rest_day"
                  ? {
                      kind: "rest_day",
                      scheduleDate,
                      ...(note.trim() ? { availabilityNote: note.trim() } : {}),
                    }
                  : dutyInput(scheduleDate, startTime, endTime, note),
              )
            }
          >
            Save cell
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
